/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Form, Input, Switch } from "core/components";
import { navigateTo } from "core/navigation";
import { UnitMeasureFormData, UnitMeasureSchema } from "core/schema";
import { admin_unit_measures_path, admin_unit_measure_path } from "routes";

type UnitMeasureFormProps = {
  unit_measure: UnitMeasureFormData;
};

const UnitMeasureForm = ({ unit_measure }: UnitMeasureFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<UnitMeasureFormData>({
    mode: "onChange",
    defaultValues: UnitMeasureSchema.cast(unit_measure),
    resolver: yupResolver(UnitMeasureSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const save = async (data: UnitMeasureFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_unit_measure_path(unit_measure), data);
      } else {
        await api.post(admin_unit_measures_path(unit_measure), data);
      }

      navigateTo(admin_unit_measures_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={unit_measure.id ? "Edit Unit of Measure" : "New Unit of Measure"}>
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_unit_measures_path())}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={3} />
                <Col>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                  <Input {...register("code")} label="Code" required errorMessage={errors.code?.message} maxLength={10} />
                  <Input {...register("name")} label="Name" required errorMessage={errors.name?.message} maxLength={100} />
                </Col>
                <Col lg={3} />
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default UnitMeasureForm;
